/*
 * Copyright 2022 VMware, Inc.
 * SPDX-License-Identifier: EPL-2.0
 */

import { VIPConfig } from "../config";
import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { LocaleService } from "./locale.service";
import { map } from "rxjs/operators";
import { ContentData } from "./content.data";
import { VIPService } from "./vip.service";

export enum ContentType {
    MD = "md",
    SVG = "svg",
}

export interface Resolver {
    /**
     * Resolve the content by key according to current locale.
     * @param key The key of content to be resolved.
     */
    resolve(key: string): Observable<string>;

    /**
     * Render interpolations using translations in a template.
     * @param data The content template and the translated values.
     */
    render(data: ContentData): string;
}

export abstract class ContentResolverFactory {
    /**
     * Return a resolver instance according to specified type.
     * @param config Determine the content namespace.
     * @param type Resolver type.
     */
    abstract getResolver(config: VIPConfig, type: ContentType): Resolver;
}

export class MDResolver implements Resolver {
    private contentCache: { [cacheKey: string]: string } = {};
    constructor(private config: VIPConfig, private vipService: VIPService, private localeService: LocaleService) {
        this.config = config;
    }

    private getCacheKey(locale: string, key: string) {
        return `${key}::${locale}`;
    }

    resolve(key: string): Observable<string> {
        let cacheKey = this.getCacheKey(this.localeService.getCurrentLocale(), key);
        return this.contentCache[cacheKey]
            ? of(this.contentCache[cacheKey])
            : this.vipService.loadContent(this.config, key).pipe(
                  map((data: ContentData) => {
                      if (!this.localeService.isSourceLocale && data.tplValue) {
                          this.composeDataUri(data);
                          this.contentCache[cacheKey] = this.render(data);
                          return this.contentCache[cacheKey];
                      }

                      this.contentCache[cacheKey] = data.tplValue || data.tplKey;
                      return this.contentCache[cacheKey];
                  })
              );
    }

    render(data: ContentData): string {
        return data.tplValue.replace(/{{(.*?)}}/g, (match, key) => {
            const assetObject = data.assets && data.assets[key.trim()];
            return assetObject ? assetObject["value"] || assetObject["path"] || key.trim() : key.trim();
        });
    }

    private composeDataUri(data: ContentData) {
        if (data.assets) {
            Object.keys(data.assets).forEach((key: string) => {
                const type = key.substring(key.lastIndexOf("-") + 1, key.length);
                switch (type) {
                    case ContentType.SVG:
                        data.assets[key]["value"] = data.assets[key]["value"]
                            ? `data:image/svg+xml,${data.assets[key]["value"]}`
                            : data.assets[key]["value"];
                        break;
                }
            });
        }
    }
}

@Injectable()
export class FakeContentResolverFactory implements ContentResolverFactory {
    public getResolver(config: VIPConfig, type: ContentType): Resolver {
        return new FakeResolver();
    }
}

export class FakeResolver implements Resolver {
    resolve(key: string): Observable<string> {
        return of("");
    }

    render(data: ContentData): string {
        return "";
    }
}

@Injectable()
export class I18nContentResolverFactory implements ContentResolverFactory {
    private resolverStore: { [key: number]: Resolver } = {};
    constructor(private vipService: VIPService, private localeService: LocaleService) {}

    public getResolver(config: VIPConfig, type: ContentType): Resolver {
        switch (type) {
            case ContentType.MD:
            default:
                this.resolverStore[type] = this.resolverStore[type] || new MDResolver(config, this.vipService, this.localeService);
                break;
        }
        return this.resolverStore[type];
    }
}
